import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Sentry } from "../lib/sentry";
import { tx as __tx } from "../lib/tx";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // Forward to Sentry when initialised (initSentry() is a no-op without
    // REACT_APP_SENTRY_DSN, so this stays silent in local dev / previews).
    try {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } },
      });
    } catch (e) {
      // Swallow telemetry failures so we don't shadow the original error.
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center min-h-screen p-8 text-center"
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle className="w-16 h-16 text-destructive mb-4" aria-hidden="true" />
          <h1 className="text-2xl font-semibold mb-2">{__tx("Something went wrong")}</h1>
          <p className="text-muted-foreground mb-6 max-w-md">
            {__tx("An unexpected error occurred. Try reloading the page.")}
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleReset} variant="outline" className="gap-2">
              <RefreshCw size={16} aria-hidden="true" />
              {__tx("Try again")}
            </Button>
            <Button onClick={() => window.location.reload()} className="gap-2">
              <RefreshCw size={16} aria-hidden="true" />
              {__tx("Reload page")}
            </Button>
          </div>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre className="mt-6 p-4 bg-muted rounded-lg text-left text-sm max-w-2xl overflow-auto">
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
